import React, { useMemo } from 'react';
import { Layer, Line, Circle, Group } from 'react-konva';
import { CELL_SIZE } from './GridLayer';

// Visual styles for each wall type. Strokes match the pixel-art tile palette;
// shadows are crisp 1-px offsets (no blur) so walls read as hard silhouettes.
export const WALL_STYLES = {
  stone: { stroke: '#8c8ca4', strokeWidth: 10, shadow: 'rgba(0,0,0,0.85)', shadowBlur: 0 },
  wood:  { stroke: '#6b4226', strokeWidth: 8,  shadow: 'rgba(0,0,0,0.8)',  shadowBlur: 0 },
  cave:  { stroke: '#4a4a5e', strokeWidth: 13, shadow: 'rgba(0,0,0,0.9)',  shadowBlur: 0 },
  brick: { stroke: '#7a3a2a', strokeWidth: 10, shadow: 'rgba(0,0,0,0.85)', shadowBlur: 0 },
  iron:  { stroke: '#3a3a4a', strokeWidth: 8,  shadow: 'rgba(0,0,0,0.8)',  shadowBlur: 0 },
};

export default function WallLayer({
  walls        = [],
  draftPts     = [],
  mousePos     = null,
  activeTool   = null,
  activeStyle  = 'stone',
  selectedWallIds = null,
  onWallClick  = null,
  onWallMouseDown = null,
  dragOffset   = null,
  onSegmentClick = null,
  onSegmentConvert = null,
  draftCurved      = false,
  bare             = false,
}) {
  const isWallPen     = activeTool === 'wall-pen';
  const isWallSelect  = activeTool === 'wall-select';
  const isWallEraser  = activeTool === 'wall-eraser';
  const isWallToDoor  = activeTool === 'wall-to-door';

  // ── Saved walls ────────────────────────────────────────────────────────────
  const wallElements = useMemo(() => walls.map((wall) => {
    const sty = WALL_STYLES[wall.style] || WALL_STYLES.stone;
    if (!wall.pts || wall.pts.length < 2) return null;
    const sel  = selectedWallIds && selectedWallIds.has(wall.id);
    const dx = sel && dragOffset ? dragOffset.x * CELL_SIZE : 0;
    const dy = sel && dragOffset ? dragOffset.y * CELL_SIZE : 0;
    const flat = wall.pts.flatMap((p) => [p.x * CELL_SIZE + dx, p.y * CELL_SIZE + dy]);
    return (
      <Line
        key={wall.id}
        points={flat}
        closed={wall.closed}
        tension={wall.curved ? 0.4 : 0}
        stroke={sel ? '#f5c842' : sty.stroke}
        strokeWidth={sel ? sty.strokeWidth + 3 : sty.strokeWidth}
        lineCap="round"
        lineJoin="round"
        shadowColor={sel ? 'rgba(245,200,66,0.6)' : sty.shadow}
        shadowBlur={sel ? 10 : sty.shadowBlur}
        shadowOffsetX={1}
        shadowOffsetY={2}
        hitStrokeWidth={24}
        onMouseDown={(e) => onWallMouseDown?.(wall.id, !!e.evt?.shiftKey, e)}
        onClick={(e) => onWallClick?.(wall.id, !!e.evt?.shiftKey)}
        listening={isWallSelect}
      />
    );
  }), [walls, selectedWallIds, dragOffset, isWallSelect, onWallClick, onWallMouseDown]);

  // ── Eraser / wall-to-door: render each segment as a separate clickable line ──
  const segmentInteractMode = isWallEraser || isWallToDoor;
  const eraserElements = useMemo(() => {
    if (!segmentInteractMode) return null;
    const hoverColor = isWallToDoor ? 'rgba(184,115,51,0.5)' : 'rgba(245,80,80,0.5)';
    const elems = [];
    for (const wall of walls) {
      const sty = WALL_STYLES[wall.style] || WALL_STYLES.stone;
      if (!wall.pts || wall.pts.length < 2) continue;
      const segs = [];
      for (let i = 0; i < wall.pts.length - 1; i++) segs.push(i);
      if (wall.closed && wall.pts.length > 2) segs.push(wall.pts.length - 1);
      for (const i of segs) {
        const a = wall.pts[i];
        const b = wall.pts[(i + 1) % wall.pts.length];
        const handler = isWallToDoor
          ? () => onSegmentConvert?.(wall.id, i, a, b, wall.style)
          : () => onSegmentClick?.(wall.id, i);
        elems.push(
          <Line
            key={`${wall.id}-seg${i}`}
            points={[a.x * CELL_SIZE, a.y * CELL_SIZE, b.x * CELL_SIZE, b.y * CELL_SIZE]}
            stroke="transparent"
            strokeWidth={sty.strokeWidth + 6}
            hitStrokeWidth={28}
            lineCap="round"
            onMouseEnter={(e) => { e.target.stroke(hoverColor); e.target.getLayer().batchDraw(); }}
            onMouseLeave={(e) => { e.target.stroke('transparent'); e.target.getLayer().batchDraw(); }}
            onClick={handler}
            listening={true}
          />
        );
      }
    }
    return elems;
  }, [segmentInteractMode, isWallToDoor, walls, onSegmentClick, onSegmentConvert]);

  // ── Draft preview ──────────────────────────────────────────────────────────
  const draftStyle    = WALL_STYLES[activeStyle] || WALL_STYLES.stone;
  const previewPts    = mousePos ? [...draftPts, mousePos] : draftPts;
  const flatPreview   = previewPts.length >= 2
    ? previewPts.flatMap((p) => [p.x * CELL_SIZE, p.y * CELL_SIZE])
    : null;

  const isNearStart = isWallPen && draftPts.length >= 3 && mousePos
    && Math.hypot(mousePos.x - draftPts[0].x, mousePos.y - draftPts[0].y) < 0.75;

  const body = (
    <>
      {wallElements}
      {eraserElements}

      {isWallPen && (
        <Group listening={false}>
          {/* Draft path line */}
          {flatPreview && (
            <Line
              points={flatPreview}
              tension={draftCurved ? 0.4 : 0}
              stroke={draftStyle.stroke}
              strokeWidth={draftStyle.strokeWidth}
              lineCap="round"
              lineJoin="round"
              opacity={0.6}
              dash={[10, 6]}
            />
          )}

          {/* Placed vertex dots */}
          {draftPts.map((pt, i) => (
            <Circle
              key={i}
              x={pt.x * CELL_SIZE}
              y={pt.y * CELL_SIZE}
              radius={i === 0 ? 7 : 3.5}
              fill={i === 0 && isNearStart ? '#4ade80' : draftStyle.stroke}
              stroke={i === 0 ? (isNearStart ? '#4ade80' : '#ffffff') : 'transparent'}
              strokeWidth={1.5}
              opacity={0.9}
            />
          ))}

          {/* Snap cursor */}
          {mousePos && (
            <Circle
              x={mousePos.x * CELL_SIZE}
              y={mousePos.y * CELL_SIZE}
              radius={5}
              fill={isNearStart ? '#4ade80' : '#ffffff'}
              opacity={0.75}
            />
          )}
        </Group>
      )}
    </>
  );

  if (bare) return body;
  return <Layer listening={isWallSelect || isWallEraser || isWallToDoor}>{body}</Layer>;
}
